"use client"

import {
  UserIcon,
  ListBulletIcon,
  EnvelopeIcon,
  Cog6ToothIcon,
  XMarkIcon,
  Bars3Icon,
  PresentationChartBarIcon,
  ChartBarSquareIcon,
} from "@heroicons/react/24/outline"
import Link from "next/link"
import React from "react"
import { usePathname, useRouter } from "next/navigation"
import { trpc } from "@sens/web/lib/trpc"
import { noConflict } from "lodash"
import clsx from "clsx"
import { Disclosure, Menu, Transition } from "@headlessui/react"
import { useSession } from "next-auth/react"
import { Session } from "next-auth"

type Props = {
  className?: string
  session: Session | null
}

export const HeaderApp: React.FC<Props> = ({ className, session }) => {
  const enabledFeatures = session?.user.enabledFeatures || []
  //   const getProfile = trpc.getProfilePrivate.useQuery()
  //   const profileId = getProfile?.data?.id
  //   const profileUrl = "/u/" + profileId

  const pathname = usePathname()

  let activeRoute = "app"
  if (pathname?.includes("/app/list")) {
    activeRoute = "list"
  } else if (pathname?.includes("/app/profile")) {
    activeRoute = "profile"
  } else if (pathname?.includes("/app/mail")) {
    activeRoute = "mail"
  } else if (pathname?.includes("/app/settings")) {
    activeRoute = "settings"
  } else if (pathname?.includes("/app/visualization")) {
    activeRoute = "visualization"
  }

  const navigation = [
    { name: "Home", href: "/app", current: activeRoute === "app", icon: null },
    {
      name: "Your Page",
      href: "/app/profile",
      current: activeRoute === "profile",
      icon: <UserIcon className="w-5 text-gray-500" />,
    },
    {
      name: "Data Collections",
      href: "/app/list",
      current: activeRoute === "list",
      icon: <ListBulletIcon className="w-5 text-gray-500" />,
    },
    {
      name: "Visualization",
      href: "/app/visualization",
      current: activeRoute === "visualization",
      icon: <ChartBarSquareIcon className="w-5 text-gray-500" />,
      hide: !enabledFeatures.includes("visualization-chat"),
    },
    {
      name: "Newsletter & Subscribers",
      href: "/app/mail",
      current: activeRoute === "mail",
      icon: <EnvelopeIcon className="w-5 text-gray-500" />,
    },
    {
      name: "Settings",
      href: "/app/settings",
      current: activeRoute === "settings",
      icon: <Cog6ToothIcon className="w-5 text-gray-500" />,
    },
  ]

  return (
    <Disclosure as="nav" className="">
      {({ open }) => (
        <>
          <div className="relative flex h-16 items-center justify-between">
            <div className="absolute inset-y-0 left-0 flex items-center md:hidden">
              {/* Mobile menu button*/}
              <Disclosure.Button className="relative inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-sky-500">
                <span className="absolute -inset-0.5" />
                <span className="sr-only">Open main menu</span>
                {open ? (
                  <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                )}
              </Disclosure.Button>
            </div>

            <ul className="hidden bg-base-100 rounded-box text-base md:flex md:space-x-4 md:py-2">
              {navigation
                .filter((n) => !n.hide)
                .map((item) => (
                  <li key={item.name}>
                    <Link
                      href={item.href}
                      className={clsx(
                        item.current
                          ? "bg-gray-100 text-gray-900  hover:bg-gray-100"
                          : "text-gray-900 hover:bg-gray-100 hover:text-gray-900",
                        "inline-flex items-center rounded-sm py-2 px-3 gap-1"
                      )}
                    >
                      {item.icon}
                      {item.name}
                    </Link>
                  </li>
                ))}
            </ul>
          </div>
          <Disclosure.Panel className="md:hidden">
            <div className="space-y-1 px-2 pb-3 pt-2">
              {navigation
                .filter((n) => !n.hide)
                .map((item) => (
                  <Disclosure.Button
                    key={item.name}
                    as="a"
                    href={item.href}
                    className={clsx(
                      item.current ? "bg-gray-100" : " hover:bg-gray-100",
                      "block rounded-md px-3 py-2 text-base font-medium"
                    )}
                    aria-current={item.current ? "page" : undefined}
                  >
                    {item.name}
                  </Disclosure.Button>
                ))}
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  )
}
